import {
  titleOptions,
  genderOptions,
  PensionDeductionMethodsEnum,
  maritalStatusOption,
  regStatusOptions,
  defaultPaymentFrequency,
  defaultPaymentMethod,
  regulationOptions,
  rightToWorKStatusOptions,
  defaultDocumentTypeOptions,
  NICategoryOptions,
  starterTypeOptions,
  starterDeclarationOptions,
  holidayPaymentMethodOptions,
  optStatePensionOptions,
  defaultRightToWorkStatus,
  TaxExclusionOptions,
  uscExemptionOptions,
  ASCCalculationMethodOptions,
  USCTiers,
  defaultRegistrationStatus,
  defaultPaymentTerm,
  defaultsBusinessTypeOptions,
  defaultCisStatus,
  defaultPartnerCategoryOptions,
  defaultMarginMethod,
  expenseClaimRuleOptions,
  isIrregularHolidayMethod,
} from '@nxsys/constants/AppConstants';
import { countryOptions } from '@nxsys/constants/countries';
import type { IrregularHolidayOpeningBalances } from '@nxsys/types/api';
import { find, isEmpty, isNil, isNumber } from 'lodash';
import moment from 'moment-timezone';
import { getFieldLabelByCountry } from './labelConfig';
import { getAppConfig } from './appConfigs';
import { prsiEeClasses } from '@nxsys/constants/psr';

export const csvConfigExport = (name: string, headers: string[]) => {
  return {
    filename:
      `${name}-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}` || 'Download.csv',
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    showTitle: false,
    title: `${name} Data Export on ${moment(new Date())
      .tz('Europe/London')
      .format('DD/MM/YYYY')}`,
    useTextFile: false,
    useBom: true,
    useKeysAsHeaders: headers?.length > 0 ? false : true,
    headers,
  };
};

export const formatYeSNoNotAvailable = (value: string) => {
  switch (value) {
    case 'TRUE':
      return 'Yes';
    case 'FALSE':
      return 'No';
    default:
      return 'Not available';
  }
};

const DATE_FORMAT = 'DD/MM/YYYY';

export const EXPORT_BULK_UPDATE_EMPLOYER_COLUMN = (payrollCountry: string) => [
  'System ID',
  'Employer Ref',
  'Onboarding Required',
  'Title',
  'Forename',
  'Surname',
  'DOB',
  'Gender',
  'Marital Status',
  `${getFieldLabelByCountry(payrollCountry, 'NINo')}`,
  'Nationality',
  'Address 1',
  'Address 2',
  'Address 3',
  'City/Town',
  'County',
  `${getFieldLabelByCountry(payrollCountry, 'postCode')}`,
  'Country',
  'Mobile',
  'Email',
  'Registration Status',
  'Pay Frequency',
  'Pay Method',
  'Job Title',
  'Annual Salary',
  'Rate/Hour',
  'Hours',
  'Working Time Regulation',
  'Department',
  'Date of Joining',
  'Allow Portal Login?',
  'Login ID',
  'Right to Work Status',
  'Document Type',
  'Document Expiry Date',
  'Bank Name',
  'Account name',
  'Account Number',
  'Sort Code',
  'Building Society Number',
  'SWIFT No',
  'IBAN Reference',
  'Tax Code',
  'Tax Basis',
  'NI Category',
  'Workplace Postcode',
  'Starter Type',
  // 'Pay ID Unknown',
  'Previous Payroll ID',
  'Starter Declaration',
  'P45 Gross To Date',
  'P45 Tax To Date',
  'Is Apprentice',
  'Employee NI YTD',
  'Employer NI YTD',
  'Employee Pension YTD',
  'Employer Pension YTD',
  'Student Loan YTD',
  'PG Student Loan YTD',
  'SMP YTD',
  // 'SNCP YTD',
  'SSP YTD',
  'SAP YTD',
  // 'ShPP YTD',
  'SPP YTD',
  'Gross to LEL',
  'Gross LEL to PT',
  'Gross PT to UEL',
  'Gross',
  'Taxable Gross',
  'Niable Gross',
  'Tax',
  'Employer',
  'Payroll Approval Needed?',
  'Holiday Pay Method',
  'Included In Timesheet Rate',
  'Holiday Pay Rate',
  'Holiday Pay Opening Balance',
  // new
  'ER pension deduction method',
  'ER %/Amount',
  'EE pension deduction method',
  'EE %/Amount',
  'Pension Method',
  'NEST Group',
  'Postponement End Date',
  'Membership ID',
  'Membership Start Date',
  'Opt State',
  'Opt-in Date',
  'Opt-out Date',
  'Category',
  'Date on which classified on',
  'Last Assessment Done On',
  ///
  'Customer Success Manager',
  'Other',
  'Emergency Contact Name',
  'Emergency Contact Address 1',
  'Emergency Contact Address 2',
  'Emergency Contact Address 3',
  'Emergency Contact City/Town',
  'Emergency Contact County',
  `Emergency Contact ${getFieldLabelByCountry(payrollCountry, 'postCode')}`,
  'Emergency Contact Country',
  'Emergency Contact Mobile',
  'Emergency Contact Email',
  'Share Code / EC Check Code',
  'Number of Weeks Worked',
];

// NX-6284: today's leave-year start date for the worker's company (day/month
// repeats every year, default 1 April when unset — same default the company
// form uses). A round-trip export must key off THIS, not "whichever entry is
// newest": before this rule, re-uploading an unchanged export after a leave
// year rolled over wrote the prior year's carry-over into the new year,
// silently adding hours (no diff check on the bulk-update side).
const currentIrregularHolidayLeaveYearStart = (
  company:
    | {
        holidayLeaveYearStartDay?: number | null;
        holidayLeaveYearStartMonth?: number | null;
      }
    | null
    | undefined,
  today: Date = new Date(),
): string => {
  const day = company?.holidayLeaveYearStartDay || 1;
  const monthIndex = (company?.holidayLeaveYearStartMonth || 4) - 1; // Date month is 0-based
  const todayUTC = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const thisYearStart = Date.UTC(today.getFullYear(), monthIndex, day);
  const year =
    todayUTC >= thisYearStart ? today.getFullYear() : today.getFullYear() - 1;
  const mm = String(monthIndex + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

// NX-6284: the Ireland 8% of worked hours (irregular) opening-balance entry
// to round-trip — the one whose leave year is the CURRENT one, from balances
// entered for the worker's current employment. The backend stamps the
// employment once, on the balances object, and ignores balances stamped with
// another employment (a rehire's previous balances must never resurface as
// this employment's); unstamped balances predate the stamp and still apply.
// No match → undefined, so the export cell stays blank rather than echoing
// stale data.
const currentIrregularHolidayEntry = (
  balances: IrregularHolidayOpeningBalances | null | undefined,
  currentLeaveYearStart: string,
  currentEmploymentId?: number | null,
) => {
  const stamped = balances?.employmentId;
  if (stamped != null && Number(stamped) !== Number(currentEmploymentId)) {
    return undefined;
  }
  return (balances?.entries || []).find(
    (e) => e.leaveYearStart === currentLeaveYearStart,
  );
};

export const EXPORT_BULK_UPDATE_EMPLOYEE_IRELAND_COLUMN = [
  'System ID',
  'Employer Ref',
  'Onboarding Required',
  'Title',
  'Forename',
  'Surname',
  'DOB',
  'Gender',
  'Marital Status',
  'PPS number',
  'Employment ID',
  'Nationality',
  'Address 1',
  'Address 2',
  'Address 3',
  'City/Town',
  'County',
  'EIR Code',
  'Country',
  'Mobile',
  'Email',
  'Registration Status',
  'Pay Frequency',
  'Pay Method',
  'Job Title',
  'Annual Salary',
  'Rate/Hour',
  'Hours',
  'Working Time Regulation',
  'Department',
  'Date of Joining',
  'Allow Portal Login?',
  'Login ID',
  'Right to Work Status',
  'Document Type',
  'Document Expiry Date',
  'Bank Name',
  'Account name',
  'Account Number',
  'Sort Code',
  'Building Society Number',
  'SWIFT No',
  'IBAN Reference',
  'Payment Model',
  'RCT Status',
  'Employee Reference Number',
  'Tax Basis',
  'PRSI Classes',
  'Taxable Gross',
  'Tax',
  'Employee PRSIable Gross YTD',
  'Employee PRSI YTD',
  'Employer PRSIable Gross YTD',
  'Employer PRSI YTD',
  'USCable Gross YTD',
  'USC YTD',
  'Employee Pension YTD',
  'Employer Pension YTD',
  'LPT YTD',
  'SSP YTD',
  'Employer',
  'Payroll Approval Needed?',
  'Holiday Pay Method',
  'Included In Timesheet Rate',
  'Holiday Pay Rate',
  'Holiday Pay Opening Balance',
  // NX-6284: 8% of worked hours (irregular), PAYE only — current leave
  // year's carry-over/additional hours, plus the usual-weekly-hours override.
  'Holiday Hours Carry Over',
  'Holiday Hours Additional',
  'Usual Weekly Hours',
  'Customer Success Manager',
  'Other',
  'Emergency Contact Name',
  'Emergency Contact Address 1',
  'Emergency Contact Address 2',
  'Emergency Contact Address 3',
  'Emergency Contact City/Town',
  'Emergency Contact County',
  'Emergency Contact EIR Code',
  'Emergency Contact Country',
  'Emergency Contact Mobile',
  'Emergency Contact Email',
  'Tax Exclusion',
  'Tax Credit',
  'Tax Cut-off point',
  'USC Exemption',
  'USC Cut-off Points',
  '0.5% Cut off points',
  '2% Cut off points',
  '4% Cut off points',
  'Total LPT to be deducted',
  'ASC calculation method',
];

// NX-5228: umbrella-Ireland bulk-update columns. Order must match the key
// order generateUmbrellaIrelandSubDataExport builds, because XLSX writes object
// keys positionally and this array overwrites the header row.
export const EXPORT_BULK_UPDATE_SUB_COLUMN_UMBRELLA_IRELAND = [
  'System ID',
  'Agency Ref',
  'Onboarding Required',
  'Title',
  'Forename',
  'Surname',
  'Primary Agency',
  'Secondary Agency',
  'DOB',
  'Gender',
  'Marital Status',
  'PPS number',
  'Employment ID',
  'Nationality',
  'Address 1',
  'Address 2',
  'Address 3',
  'City/Town',
  'County',
  'EIR Code',
  'Country',
  'Mobile',
  'Email',
  'Registration Status',
  'Pay Frequency',
  'Pay Method',
  'Job Title',
  'Annual Salary',
  'Rate/Hour',
  'Hours',
  'Working Time Regulation',
  'Department',
  'Date of Joining',
  'Allow Portal Login?',
  'Login ID',
  'Right to Work Status',
  'Document Type',
  'Document Expiry Date',
  'Bank Name',
  'Account name',
  'Account Number',
  'Sort Code',
  'Building Society Number',
  'SWIFT No',
  'IBAN Reference',
  'Agency Conduct Regulation',
  'Payment Term',
  'Payment Model',
  'RCT Status',
  'Employee Reference Number',
  'Tax Basis',
  'PRSI Classes',
  'Taxable Gross',
  'Tax',
  'Employee PRSIable Gross YTD',
  'Employee PRSI YTD',
  'Employer PRSIable Gross YTD',
  'Employer PRSI YTD',
  'USCable Gross YTD',
  'USC YTD',
  'Employee Pension YTD',
  'Employer Pension YTD',
  'LPT YTD',
  'SSP YTD',
  'Payment Company',
  'Margin Method',
  'Margin',
  'Minimal Margin',
  'Margin Capped',
  'Assignment Required?',
  'Timesheet Approval Needed?',
  'Timesheet Approver',
  'Payroll Approval Needed?',
  'Holiday Pay Method',
  'Included In Timesheet Rate',
  'Holiday Pay Rate',
  'Holiday Pay Opening Balance',
  // NX-6284: 8% of worked hours (irregular), PAYE only — current leave year
  // carry-over/additional, plus the usual-weekly-hours override. Blank on an
  // umbrella row: the method never applies there, and an umbrella-product
  // tenant mixes PAYE and Umbrella workers in one sheet.
  'Holiday Hours Carry Over',
  'Holiday Hours Additional',
  'Usual Weekly Hours',
  'Customer Success Manager',
  'Agency Consultant',
  'Sales Consultant',
  'Other',
  'Emergency Contact Name',
  'Emergency Contact Address 1',
  'Emergency Contact Address 2',
  'Emergency Contact Address 3',
  'Emergency Contact City/Town',
  'Emergency Contact County',
  'Emergency Contact EIR Code',
  'Emergency Contact Country',
  'Emergency Contact Mobile',
  'Emergency Contact Email',
  'Tax Exclusion',
  'Tax Credit',
  'Tax Cut-off point',
  'USC Exemption',
  'USC Cut-off Points',
  '0.5% Cut off points',
  '2% Cut off points',
  '4% Cut off points',
  'Total LPT to be deducted',
  'ASC calculation method',
];

export const EXPORT_BULK_UPDATE_SUB_COLUMN_UMBRELLA = [
  'System ID',
  'Agency Ref',
  'Onboarding Required',
  'Title',
  'Forename',
  'Surname',
  'Primary Agency',
  'Secondary Agency',
  'DOB',
  'Gender',
  'Marital Status',
  'NI number',
  'Nationality',
  'Address 1',
  'Address 2',
  'Address 3',
  'City/Town',
  'County',
  'Post Code',
  'Country',
  'Mobile',
  'Email',
  'Registration Status',
  'Pay Frequency',
  'Pay Method',
  'Job Description',
  'Rate/Hour',
  'Hours',
  'Working Time Regulation',
  'Agency Conduct Regulation',
  'Date of Joining',
  'Allow Portal Login?',
  'Login ID',
  'Allow Rewards',
  'Right to Work Status',
  'Document Type',
  'Document Expiry Date',
  'Bank Name',
  'Account name',
  'Account Number',
  'Sort Code',
  'Building Society Number',
  'SWIFT No',
  'IBAN Reference',
  'Payment Term',
  'Payment Model',
  'Business Type',
  'CIS Status',
  'Personal Verification Number',
  'VAT Reg No',
  'Verification Date',
  'Match Date',
  'Company Name',
  'Registration Number',
  'UTR No',
  'Firm Name',
  'Partner Name',
  'Partner Category',
  'Tax Code',
  'Tax Basis',
  'NI Category',
  'Workplace Postcode',
  'Starter Type',
  // 'Pay ID Unknown',
  'Previous Payroll ID',
  'Starter Declaration',
  'P45 Gross To Date',
  'P45 Tax To Date',
  'Is Apprentice',
  'Employee NI YTD',
  'Employer NI YTD',
  'Employee Pension YTD',
  'Employer Pension YTD',
  'Student Loan YTD',
  'PG Student Loan YTD',
  'SMP YTD',
  // 'SNCP YTD',
  'SSP YTD',
  'SAP YTD',
  // 'ShPP YTD',
  'SPP YTD',
  'Gross to LEL',
  'Gross LEL to PT',
  'Gross PT to UEL',
  'Gross',
  'Taxable Gross',
  'Niable Gross',
  'Tax',
  'Payment Company',
  'Margin Method',
  'Margin',
  'Minimal Margin',
  'Margin Capped',
  'Assignment Required?',
  'Timesheet Approval Needed?',
  'Timesheet Approver',
  'Holiday Pay Method',
  'Included In Timesheet Rate',
  'Holiday Pay Rate',
  'Reclaim App Levy Costs',
  'Holiday Pay Opening Balance',
  'Holiday Pay App Levy',
  'Holiday Pay ERNI',
  "Holiday Pay Employer's Pension",
  'Expense Claim Rule',
  'Expense Receipt Upload Mandatory',
  'Expense Opening Balance',
  'Outside UK Worker',
  'ER pension deduction method',
  'ER %/Amount',
  'EE pension deduction method',
  'EE %/Amount',
  'Pension Method',
  'NEST Group',
  'Date on which classified on',
  'Last Assessment Done On',
  'Postponement End Date',
  'Membership ID',
  'Membership Start Date',
  'Opt State',
  'Opt-in Date',
  'Opt-out Date',
  'Category',
  'Customer Success Manager',
  'Agency Consultant',
  'Sales Consultant',
  'Other',
  'Emergency Contact Name',
  'Emergency Contact Address 1',
  'Emergency Contact Address 2',
  'Emergency Contact Address 3',
  'Emergency Contact Mobile',
  'Emergency Contact Email',
  'Emergency Contact City/Town',
  'Emergency Contact County',
  'Emergency Contact Post Code',
  'Emergency Contact Country',
  'Share Code / EC Check Code',
  'Off payroll worker',
  'Irregular Paid Worker',
  // 'Restricted Hours?',
  'Number of Weeks Worked',
];

export const generateSubDataExportBureau = (data: any[]) => {
  return data.map((item: any) => {
    const {
      id,
      title,
      gender,
      DOB,
      agencyRef,
      forename,
      surname,
      mobile,
      email,
      address,
      city,
      county,
      country,
      postCode,
      emergencyName,
      emergencyAddress,
      emergencyCity,
      emergencyCounty,
      emergencyPostCode,
      emergencyCountry,
      emergencyMobile,
      emergencyEmail,
      payFrequency,
      registrationStatus,
      maritalStatus,
      NINumber,
      nationality,
      payMethod,
      jobDescription,
      rateHour,
      hours,
      dateOfJoining,
      allowPortalLogin,
      loginId,
      rightToWorkStatus,
      documentType,
      documentExpiryDate,
      bankRef,
      isOnboarding,
      setting,
      annualSalary,
      companyPayment,
      pensionSetting,
      erPensionDeductionMethod,
      eePensionDeductionMethod,
      erPensionFixedAmount,
      eePensionFixedAmount,
      erPensionPercentage,
      eePensionPercentage,
      department,
      shareCodeVerified,
      workingTimeRegulation,
      pensionMethodText,
      nestGroupText,
    } = item;
    const p11 = setting?.p11 || {};
    const erPensionPercentageOrAmount =
      erPensionDeductionMethod === PensionDeductionMethodsEnum.FIXED
        ? erPensionFixedAmount
        : erPensionPercentage;
    const eePensionPercentageOrAmount =
      eePensionDeductionMethod === PensionDeductionMethodsEnum.FIXED
        ? eePensionFixedAmount
        : eePensionPercentage;
    const exportData = {
      id: id || '',
      agencyRef: agencyRef || '',
      onBoardingRequired: getBooleanValue(isOnboarding),
      title: title
        ? find(titleOptions, (t: any) => t.value === title)?.label || ''
        : '',
      forename: forename || '',
      surname: surname || '',
      DOB: DOB ? moment(DOB).tz('Europe/London').format(DATE_FORMAT) : '',
      gender: gender
        ? find(genderOptions, (t) => t.value === gender)?.label || ''
        : '',
      maritalStatus: maritalStatus
        ? find(maritalStatusOption, (m) => m.value === maritalStatus)?.label ||
        ''
        : '',
      NINumber: NINumber || '',
      nationality: nationality || '',
      address1: !isEmpty(address) ? address[0] || '' : '',
      address2: !isEmpty(address) ? address[1] || '' : '',
      address3: !isEmpty(address) ? address[2] || '' : '',
      city: city || '',
      county: county || '',
      postCode: postCode || '',
      country: country
        ? find(countryOptions, (item) => item?.value === country)?.label
        : '',
      mobile: mobile || '',
      email: email || '',
      registrationStatus: registrationStatus
        ? find(regStatusOptions, (reg) => reg.value === registrationStatus)
          ?.label
        : '',
      payFrequency: payFrequency
        ? find(defaultPaymentFrequency, (reg) => reg.value === payFrequency)
          ?.label
        : '',
      payMethod: payMethod
        ? find(defaultPaymentMethod, (p) => p.value === payMethod)?.label
        : '',
      jobDescription: jobDescription || '',
      annualSalary: annualSalary || '',
      rateHour: rateHour || '',
      hours: hours || '',
      workingTimeRegulation: workingTimeRegulation
        ? find(
          regulationOptions,
          (item) => item.value === workingTimeRegulation,
        )?.label
        : '',
      department: department || '',
      dateOfJoining: dateOfJoining
        ? moment(dateOfJoining).tz('Europe/London').format(DATE_FORMAT)
        : '',
      allowPortalLogin: allowPortalLogin ? 'Yes' : 'No',
      loginId: loginId || '',
      rightToWorkStatus: rightToWorkStatus
        ? find(rightToWorKStatusOptions, (r) => r.value === rightToWorkStatus)
          ?.label
        : '',
      documentType: documentType
        ? find(defaultDocumentTypeOptions, (d) => d.value === documentType)
          ?.label
        : '',
      documentExpiryDate: documentExpiryDate
        ? moment(documentExpiryDate).tz('Europe/London').format(DATE_FORMAT)
        : '',
      bankName: bankRef?.bankName || '',
      accountName: bankRef?.accountName || '',
      accountNumber: bankRef?.accountNumber || '',
      sortCode: bankRef?.sortCode || '',
      buildingSocietyNumber: bankRef?.buildingSocietyNumber || '',
      SWIFTNo: bankRef?.SWIFTNo || '',
      IBANReference: bankRef?.IBANReference || '',
      taxCode: setting?.taxCode || '',
      taxBasis: setting?.taxBasis === 'W1M1' ? 'W1M1' : 'Cumulative',
      niCategory: setting?.niCategory
        ? find(NICategoryOptions, (item) => item.value === setting?.niCategory)
          ?.label
        : '',
      workplacePostcode: setting?.workplacePostcode || '',
      starterType: setting?.starterType
        ? find(starterTypeOptions, (st) => st.value === setting?.starterType)
          ?.label
        : '',
      previousPayrollNo: setting?.previousPayrollNo || '',
      starterDeclaration: setting?.starterDeclaration
        ? find(
          starterDeclarationOptions,
          (st) => st.value === setting?.starterDeclaration,
        )?.label
        : '',
      p45GrossToDate: setting?.p45GrossToDate || '',
      p45TaxToDate: setting?.p45TaxToDate || '',
      isApprentice: setting?.isApprentice ? 'Yes' : 'No',
      eeNI: typeof p11?.eeNI === 'number' ? p11?.eeNI : '',
      erNI: typeof p11?.erNI === 'number' ? p11?.erNI : '',
      eePension: typeof p11?.eePension === 'number' ? p11?.eePension : '',
      erPension: typeof p11?.erPension === 'number' ? p11?.erPension : '',
      stLoan: typeof p11?.stLoan === 'number' ? p11?.stLoan : '',
      pgLoan: typeof p11?.pgLoan === 'number' ? p11?.pgLoan : '',
      smp: typeof p11?.smp === 'number' ? p11?.smp : '',
      ssp: typeof p11?.ssp === 'number' ? p11?.ssp : '',
      sap: typeof p11?.sap === 'number' ? p11?.sap : '',
      // shpp: typeof p11?.shpp === 'number' ? p11?.shpp : '',
      spp: typeof p11?.spp === 'number' ? p11?.spp : '',
      grossToLel: typeof p11?.grossToLel === 'number' ? p11?.grossToLel : '',
      grossLelToPt:
        typeof p11?.grossLelToPt === 'number' ? p11?.grossLelToPt : '',
      grossPtToUEL:
        typeof p11?.grossPtToUEL === 'number' ? p11?.grossPtToUEL : '',
      gross: p11?.gross === 'number' ? p11?.gross : '',
      grossForTax: p11?.grossForTax === 'number' ? p11?.grossForTax : '',
      grossForNI: p11?.grossForNI === 'number' ? p11?.grossForNI : '',
      tax: p11?.tax === 'number' ? p11?.tax : '',
      employer: companyPayment?.id || '',
      payrollApproveNeeded: setting?.payrollApproveNeeded
        ? formatYeSNoNotAvailable(setting?.payrollApproveNeeded)
        : '',
      holidayPayMethod: setting?.holidayPayMethod
        ? find(
          holidayPaymentMethodOptions,
          (item) => item.value === setting?.holidayPayMethod,
        )?.label
        : '',
      includeInTimesheetRate: isNil(setting?.includeInTimesheetRate)
        ? ''
        : setting?.includeInTimesheetRate
          ? 'Yes'
          : 'No',
      holidayPayRate: setting?.holidayPayRate || '',
      holidayPayOpeningBalance: setting?.holidayPayOpeningBalance || '',
      ////// new
      erPensionDeductionMethod: erPensionDeductionMethod || '',
      erPensionPercentageOrAmount: erPensionPercentageOrAmount ?? '',
      eePensionDeductionMethod: eePensionDeductionMethod || '',
      eePensionPercentageOrAmount: eePensionPercentageOrAmount ?? '',
      pensionMethodText: pensionMethodText || '',
      nestGroupText: nestGroupText || '',
      postponementEndDate: pensionSetting?.postponementEndDate
        ? moment(pensionSetting?.postponementEndDate).format(DATE_FORMAT)
        : '',
      memberId: pensionSetting?.memberId || '',
      membershipStartDate: pensionSetting?.membershipStartDate
        ? moment(pensionSetting?.membershipStartDate).format(DATE_FORMAT)
        : '',
      otpState: pensionSetting?.otpState
        ? find(
          optStatePensionOptions,
          (item) => item.value === pensionSetting?.otpState,
        )?.label
        : '',
      otpInDate: pensionSetting?.otpInDate
        ? moment(pensionSetting?.otpInDate).format(DATE_FORMAT)
        : '',
      otpOutDate: pensionSetting?.otpOutDate
        ? moment(pensionSetting?.otpOutDate).format(DATE_FORMAT)
        : '',
      category: pensionSetting?.category || '',
      dateOnClassifiedOn: pensionSetting?.dateOnClassifiedOn
        ? moment(pensionSetting?.dateOnClassifiedOn).format(DATE_FORMAT)
        : '',
      lastAssessmentOn: pensionSetting?.lastAssessmentOn
        ? moment(pensionSetting?.lastAssessmentOn).format(DATE_FORMAT)
        : '',
      ////////
      customerSuccessManager: '',
      other: '',
      emergencyName: emergencyName || '',
      emergencyAddress1: emergencyAddress ? emergencyAddress[0] : '',
      emergencyAddress2: emergencyAddress ? emergencyAddress[1] : '',
      emergencyAddress3: emergencyAddress ? emergencyAddress[2] : '',
      emergencyCity: emergencyCity || '',
      emergencyCounty: emergencyCounty || '',
      emergencyPostCode: emergencyPostCode || '',
      emergencyCountry: emergencyCountry
        ? find(countryOptions, (item) => item?.value === emergencyCountry)
          ?.label
        : '',
      emergencyMobile: emergencyMobile || '',
      emergencyEmail: emergencyEmail || '',
      shareCodeVerified: shareCodeVerified || '',
      noOfWeeksWorked: setting?.noOfWeeksWorked || '',
    };
    return exportData;
  });
};

export const generateIrelandSubDataExport = (data: any[]) => {
  return data.map((item) => {
    const {
      id,
      title,
      gender,
      DOB,
      agencyRef,
      forename,
      surname,
      mobile,
      email,
      address,
      city,
      county,
      country,
      postCode,
      emergencyName,
      emergencyAddress,
      emergencyCity,
      emergencyCounty,
      emergencyPostCode,
      emergencyCountry,
      emergencyMobile,
      emergencyEmail,
      payFrequency,
      registrationStatus,
      maritalStatus,
      nationality,
      payMethod,
      jobDescription,
      rateHour,
      hours,
      dateOfJoining,
      allowPortalLogin,
      loginId,
      rightToWorkStatus,
      documentType,
      documentExpiryDate,
      bankRef,
      isOnboarding,
      setting,
      annualSalary,
      companyPayment,
      department,
      workingTimeRegulation,
    } = item;
    const p11 = setting?.p11 || {};
    // NX-6284: the carry-over/additional columns are PAYE-only, and only mean
    // something for the entry matching the worker's CURRENT leave year and
    // CURRENT employment — the backend rejects a row that fills them in for a
    // worker not on the method at all (bulk-update-irregular-holiday-columns.ts),
    // and a stale or wrong-employment entry must stay blank rather than
    // round-trip as this year's balance (that previously added phantom hours
    // after a leave-year rollover or a rehire).
    const usesIrregularHoliday = isIrregularHolidayMethod(
      setting?.holidayPayMethod,
    );
    const isUmbrellaRow = setting?.paymentModel === 'UMBRELLA';
    // `setting.employmentId` is the Irish Employment ID text, not the
    // employment record the balances are stamped with.
    const irregularHolidayEntry = currentIrregularHolidayEntry(
      setting?.irregularHolidayOpeningBalances,
      currentIrregularHolidayLeaveYearStart(companyPayment),
      item.currentEmploymentId,
    );
    const exportData = {
      id: id || '',
      agencyRef: agencyRef || '',
      onBoardingRequired: getBooleanValue(isOnboarding),
      title: title
        ? find(titleOptions, (t) => t.value === title)?.label || ''
        : '',
      forename: forename || '',
      surname: surname || '',
      DOB: DOB ? moment(DOB).tz('Europe/London').format(DATE_FORMAT) : '',
      gender: gender
        ? find(genderOptions, (t) => t.value === gender)?.label || ''
        : '',
      maritalStatus: maritalStatus
        ? find(maritalStatusOption, (m) => m.value === maritalStatus)?.label ||
        ''
        : '',

      ppsNumber: setting?.ppsNumber || '',
      employmentId: setting?.employmentId || '',
      nationality: nationality || '',
      address1: !isEmpty(address) ? address[0] || '' : '',
      address2: !isEmpty(address) ? address[1] || '' : '',
      address3: !isEmpty(address) ? address[2] || '' : '',
      city: city || '',
      county: county || '',
      postCode: postCode || '',
      country: country
        ? find(countryOptions, (item) => item?.value === country)?.label
        : '',
      mobile: mobile || '',
      email: email || '',
      registrationStatus: registrationStatus
        ? find(regStatusOptions, (reg) => reg.value === registrationStatus)
          ?.label
        : '',
      payFrequency: payFrequency
        ? find(defaultPaymentFrequency, (reg) => reg.value === payFrequency)
          ?.label
        : '',
      payMethod: payMethod
        ? find(defaultPaymentMethod, (p) => p.value === payMethod)?.label
        : '',
      jobDescription: jobDescription || '',
      annualSalary: annualSalary || '',
      rateHour: rateHour || '',
      hours: hours || '',
      workingTimeRegulation: workingTimeRegulation
        ? find(
          regulationOptions,
          (item) => item.value === workingTimeRegulation,
        )?.label
        : '',
      department: department || '',
      dateOfJoining: dateOfJoining
        ? moment(dateOfJoining).tz('Europe/London').format(DATE_FORMAT)
        : '',
      allowPortalLogin: allowPortalLogin ? 'Yes' : 'No',
      loginId: loginId || '',
      rightToWorkStatus: rightToWorkStatus
        ? find(defaultRightToWorkStatus, (r) => r.value === rightToWorkStatus)
          ?.label
        : '',
      documentType: documentType
        ? find(defaultDocumentTypeOptions, (d) => d.value === documentType)
          ?.label
        : '',
      documentExpiryDate: documentExpiryDate
        ? moment(documentExpiryDate).tz('Europe/London').format(DATE_FORMAT)
        : '',
      bankName: bankRef?.bankName || '',
      accountName: bankRef?.accountName || '',
      accountNumber: bankRef?.accountNumber || '',
      sortCode: bankRef?.sortCode || '',
      buildingSocietyNumber: bankRef?.buildingSocietyNumber || '',
      SWIFTNo: bankRef?.SWIFTNo || '',
      IBANReference: bankRef?.IBANReference || '',
      paymentModel:
        setting?.paymentModel === 'CIS'
          ? 'RCT'
          : setting?.paymentModel === 'PAYEE'
            ? 'PAYE'
            : setting?.paymentModel || '',
      cisStatus: setting?.cisStatus || '',
      subcontractorRefNumber: setting?.subcontractorRefNumber || '',
      taxBasis: getTaxBasic(setting?.taxBasis) || '',
      prsiEeClasses: prsiEeClasses.find((p) => p.value === setting?.prsiEeClasses)?.label || '',
      grossForTax: typeof p11?.grossForTax === 'number' ? p11?.grossForTax : '',
      tax: typeof p11?.tax === 'number' ? p11?.tax : '',
      eePrsiGross: typeof p11?.eePrsiGross === 'number' ? p11?.eePrsiGross : '',
      eePRSI: typeof p11?.eePRSI === 'number' ? p11?.eePRSI : '',
      erPrsiGross: typeof p11?.erPrsiGross === 'number' ? p11?.erPrsiGross : '',
      erPRSI: typeof p11?.erPRSI === 'number' ? p11?.erPRSI : '',
      uscGross: typeof p11?.uscGross === 'number' ? p11?.uscGross : '',
      usc: typeof p11?.usc === 'number' ? p11?.usc : '',
      eePensionYTD: typeof p11?.eePension === 'number' ? p11?.eePension : '',
      erPensionYTD: typeof p11?.erPension === 'number' ? p11?.erPension : '',
      lptDeducted: typeof p11?.lptDeducted === 'number' ? p11?.lptDeducted : '',
      sspYTD: typeof p11?.ssp === 'number' ? p11?.ssp : '',
      employer: companyPayment?.id || '', ///////////,
      payrollApproveNeeded: setting?.payrollApproveNeeded
        ? formatYeSNoNotAvailable(setting?.payrollApproveNeeded)
        : '',
      holidayPayMethod: setting?.holidayPayMethod
        ? find(
          holidayPaymentMethodOptions,
          (item) => item.value === setting?.holidayPayMethod,
        )?.label
        : '',
      includeInTimesheetRate: isNil(setting?.includeInTimesheetRate)
        ? ''
        : setting?.includeInTimesheetRate
          ? 'yes'
          : 'No',
      holidayPayRate: setting?.holidayPayRate || '',
      holidayPayOpeningBalance: setting?.holidayPayOpeningBalance || '',
      // NX-6284: 8% of worked hours (irregular), PAYE rows on the method only
      // — blank for an umbrella row or a worker on any other method.
      irregularHolidayCarryOverHours:
        usesIrregularHoliday && !isUmbrellaRow
          ? (irregularHolidayEntry?.carryOverHours ?? '')
          : '',
      irregularHolidayAdditionalHours:
        usesIrregularHoliday && !isUmbrellaRow
          ? (irregularHolidayEntry?.additionalHours ?? '')
          : '',
      // Usual weekly hours override — applies to both PAYE and umbrella.
      irregularHolidayUsualWeeklyHours:
        setting?.irregularHolidayUsualWeeklyHours ?? '',
      customerSuccessManager: '',
      other: '',
      emergencyName: emergencyName || '',
      emergencyAddress1: emergencyAddress ? emergencyAddress[0] : '',
      emergencyAddress2: emergencyAddress ? emergencyAddress[1] : '',
      emergencyAddress3: emergencyAddress ? emergencyAddress[2] : '',
      emergencyCity: emergencyCity || '',
      emergencyCounty: emergencyCounty || '',
      emergencyPostCode: emergencyPostCode || '',
      emergencyCountry: emergencyCountry
        ? find(countryOptions, (item) => item?.value === emergencyCountry)
          ?.label
        : '',
      emergencyMobile: emergencyMobile || '',
      emergencyEmail: emergencyEmail || '',
      taxExclusion: setting?.taxExclusion
        ? find(TaxExclusionOptions, (op) => op.value === setting?.taxExclusion)
          ?.label
        : '',
      taxCredit: !isNil(setting?.taxCreditAnnual)
        ? setting?.taxCreditAnnual
        : '',
      taxCutoffPoint: setting?.taxRateCutOffPoints?.[0]?.yearlyRateCutOff ?? '',
      USCExemption: setting?.uscExemption
        ? find(uscExemptionOptions, (op) => op.value === setting?.uscExemption)
          ?.label
        : '',
      USCCut_offPoints: setting?.cutOffPoints?.type
        ? find(USCTiers, (op) => op.value === setting?.cutOffPoints?.type)
          ?.label
        : '',
      CutOffPoints05:
        find(
          setting?.cutOffPoints?.values || [],
          (cop) => cop?.uscRatePercent === 0.5,
        )?.yearlyUSCRateCutOff || '',
      CutOffPoint2:
        find(
          setting?.cutOffPoints?.values || [],
          (cop) => cop?.uscRatePercent === 2,
        )?.yearlyUSCRateCutOff ?? '',
      CutOffPoints4:
        find(
          setting?.cutOffPoints?.values || [],
          (cop) => cop?.uscRatePercent === 4,
        )?.yearlyUSCRateCutOff ?? '',
      TotalLPTToBeDeducted: setting?.totalLPTToBeDeducted ?? '',
      ASCCalculationMethod:
        find(
          ASCCalculationMethodOptions,
          (op) => op?.value === setting?.ascCalculationMethod,
        )?.label || '',
    };

    return exportData;
  });
};

export const getTaxBasic = (taxBasis: string) => {
  switch (taxBasis) {
    case 'W1M1':
      return 'W1M1';
    case 'CUMULATIVE':
      return 'Cumulative';
    case 'EMERGENCY':
      return 'Emergency';
    default:
      return '';
  }
};

export const getBooleanValue = (value: boolean) => {
  return !isNil(value) ? (value ? 'Yes' : 'No') : '';
};

export const getTimeSheetApprovalNeededFormat = (value: string) => {
  switch (value) {
    case 'TRUE':
      return 'Yes';
    case 'FALSE':
      return 'No';
    default:
      return 'Not available';
  }
};

/**
 * NX-5228: the umbrella-Ireland bulk-update export.
 *
 * Before this, the export fell back to the UK umbrella shape for an Irish
 * umbrella tenant — NI number, Tax Code, NI Category, Employee/Employer NI YTD,
 * Student Loan YTD — while the on-screen table in the same module already
 * branched on country. Screen and spreadsheet disagreed, and re-uploading the
 * file could not carry PRSI/USC/LPT because no column held them.
 *
 * Composed from the bureau-Ireland row rather than written from scratch, so the
 * Irish identity and tax fields stay in one place: take that row, drop the
 * bureau-only Employer, and splice in the umbrella commercial fields using the
 * same expressions generateSubDataExportUmbrella uses. Key order matters —
 * XLSX writes object keys positionally and the header row is overwritten by
 * EXPORT_BULK_UPDATE_SUB_COLUMN_UMBRELLA_IRELAND — so the object is rebuilt in
 * order instead of spread-merged.
 */
export const generateUmbrellaIrelandSubDataExport = (data: any[]) => {
  const irelandRows = generateIrelandSubDataExport(data);

  return data.map((item, index) => {
    const row: any = { ...irelandRows[index] };
    delete row.employer; // bureau-only column

    const {
      primaryAgency,
      secondaryAgency,
      agencyConductRegulation,
      companyPayment,
      setting,
      timesheetApprover,
    } = item || {};

    const umbrella = {
      primaryAgency: primaryAgency?.name || '',
      secondaryAgency: secondaryAgency?.name || '',
      agencyConductRegulation,
      paymentTerm: setting?.paymentTerm,
      companyPayment: companyPayment?.id || '',
      marginMethod: setting?.marginMethod,
      margin: setting?.margin || '',
      minMargin: setting?.minMargin || '',
      marginCap: setting?.marginCapped || '',
      assignmentRequired: getBooleanValue(setting?.assignmentRequired),
      timesheetApprovalNeeded: getTimeSheetApprovalNeededFormat(
        setting?.timesheetApprovalNeeded,
      ),
      // Same expression as the UK umbrella row: the count written here before
      // re-uploaded as an approver called "0".
      timesheetApprover: timesheetApprover?.length
        ? timesheetApprover.map((ag: any) => ag.name).join(',')
        : null,
      agencyConsultant: '',
      salesConsultant: '',
    };

    // Rebuild in column order: identity -> agencies -> the Irish body ->
    // umbrella commercials -> the Irish tail.
    const ordered: any = {};
    const put = (k: string, v: any) => {
      ordered[k] = v;
    };

    put('id', row.id);
    put('agencyRef', row.agencyRef);
    put('onBoardingRequired', row.onBoardingRequired);
    put('title', row.title);
    put('forename', row.forename);
    put('surname', row.surname);
    put('primaryAgency', umbrella.primaryAgency);
    put('secondaryAgency', umbrella.secondaryAgency);

    const irelandBody = [
      'DOB', 'gender', 'maritalStatus', 'ppsNumber', 'employmentId',
      'nationality', 'address1', 'address2', 'address3', 'city', 'county',
      'postCode', 'country', 'mobile', 'email', 'registrationStatus',
      'payFrequency', 'payMethod', 'jobDescription', 'annualSalary', 'rateHour',
      'hours', 'workingTimeRegulation', 'department', 'dateOfJoining',
      'allowPortalLogin', 'loginId', 'rightToWorkStatus', 'documentType',
      'documentExpiryDate', 'bankName', 'accountName', 'accountNumber',
      'sortCode', 'buildingSocietyNumber', 'SWIFTNo', 'IBANReference',
    ];
    irelandBody.forEach((k) => put(k, row[k]));

    put('agencyConductRegulation', umbrella.agencyConductRegulation);
    put('paymentTerm', umbrella.paymentTerm);

    const irelandTax = [
      'paymentModel', 'cisStatus', 'subcontractorRefNumber', 'taxBasis',
      'prsiEeClasses', 'grossForTax', 'tax', 'eePrsiGross', 'eePRSI',
      'erPrsiGross', 'erPRSI', 'uscGross', 'usc', 'eePensionYTD',
      'erPensionYTD', 'lptDeducted', 'sspYTD',
    ];
    irelandTax.forEach((k) => put(k, row[k]));

    put('companyPayment', umbrella.companyPayment);
    put('marginMethod', umbrella.marginMethod);
    put('margin', umbrella.margin);
    put('minMargin', umbrella.minMargin);
    put('marginCap', umbrella.marginCap);
    put('assignmentRequired', umbrella.assignmentRequired);
    put('timesheetApprovalNeeded', umbrella.timesheetApprovalNeeded);
    put('timesheetApprover', umbrella.timesheetApprover);

    const irelandTail = [
      'payrollApproveNeeded', 'holidayPayMethod', 'includeInTimesheetRate',
      'holidayPayRate', 'holidayPayOpeningBalance',
    ];
    irelandTail.forEach((k) => put(k, row[k]));

    // NX-6284: 8% of worked hours (irregular), PAYE only — blank on an
    // umbrella row (carried from the base row above, which already applies
    // that rule). The usual-weekly-hours override is independent of payment
    // model, so it is carried over unconditionally.
    put('irregularHolidayCarryOverHours', row.irregularHolidayCarryOverHours);
    put('irregularHolidayAdditionalHours', row.irregularHolidayAdditionalHours);
    put('irregularHolidayUsualWeeklyHours', row.irregularHolidayUsualWeeklyHours);
    put('customerSuccessManager', row.customerSuccessManager);

    put('agencyConsultant', umbrella.agencyConsultant);
    put('salesConsultant', umbrella.salesConsultant);

    const irelandEnd = [
      'other', 'emergencyName', 'emergencyAddress1', 'emergencyAddress2',
      'emergencyAddress3', 'emergencyCity', 'emergencyCounty',
      'emergencyPostCode', 'emergencyCountry', 'emergencyMobile',
      'emergencyEmail', 'taxExclusion', 'taxCredit', 'taxCutoffPoint',
      'USCExemption', 'USCCut_offPoints', 'CutOffPoints05', 'CutOffPoint2',
      'CutOffPoints4', 'TotalLPTToBeDeducted', 'ASCCalculationMethod',
    ];
    irelandEnd.forEach((k) => put(k, row[k]));

    return ordered;
  });
};

export const generateSubDataExportUmbrella = (data: any[]) => {
  const defaultPaymentModel = getAppConfig('paymentModels');

  return data.map((item) => {
    const {
      id,
      title,
      gender,
      DOB,
      agencyRef,
      forename,
      surname,
      mobile,
      email,
      address,
      city,
      county,
      country,
      postCode,
      emergencyName,
      emergencyAddress,
      emergencyCity,
      emergencyCounty,
      emergencyPostCode,
      emergencyCountry,
      emergencyMobile,
      emergencyEmail,
      payFrequency,
      registrationStatus,
      maritalStatus,
      NINumber,
      nationality,
      payMethod,
      jobDescription,
      rateHour,
      hours,
      dateOfJoining,
      allowPortalLogin,
      loginId,
      rightToWorkStatus,
      documentType,
      documentExpiryDate,
      bankRef,
      isOnboarding,
      primaryAgency,
      secondaryAgency,
      companyPayment,
      setting,
      pensionSetting,
      erPensionDeductionMethod,
      eePensionDeductionMethod,
      erPensionFixedAmount,
      eePensionFixedAmount,
      erPensionPercentage,
      eePensionPercentage,
      shareCodeVerified,
      agencyConductRegulation,
      workingTimeRegulation,
      timesheetApprover,
      pensionMethodText,
      nestGroupText,
      // isRestrictedHours,
    } = item;
    const p11 = setting?.p11 || {};
    const erPensionPercentageOrAmount =
      erPensionDeductionMethod === PensionDeductionMethodsEnum.FIXED
        ? erPensionFixedAmount
        : erPensionPercentage;
    const eePensionPercentageOrAmount =
      eePensionDeductionMethod === PensionDeductionMethodsEnum.FIXED
        ? eePensionFixedAmount
        : eePensionPercentage;
    const exportData = {
      id: id || '',
      agencyRef: agencyRef || '',
      isOnboarding: getBooleanValue(isOnboarding),
      title: title
        ? find(titleOptions, (t) => t.value === title)?.label || ''
        : '',
      forename: forename || '',
      surname: surname || '',
      primaryAgency: primaryAgency?.name || '',
      secondaryAgency: secondaryAgency?.name || '',
      DOB: DOB ? moment(DOB).format(DATE_FORMAT) : '',
      gender: gender
        ? find(genderOptions, (t) => t.value === gender)?.label || ''
        : '',
      maritalStatus: maritalStatus
        ? find(maritalStatusOption, (m) => m.value === maritalStatus)?.label ||
        ''
        : '',
      NINumber: NINumber || '',
      nationality: nationality || '',
      address1: !isEmpty(address) ? address[0] || '' : '',
      address2: !isEmpty(address) ? address[1] || '' : '',
      address3: !isEmpty(address) ? address[2] || '' : '',
      city: city || '',
      county: county || '',
      postCode: postCode || '',
      country: country
        ? find(countryOptions, (item) => item?.value === country)?.label
        : '',
      mobile: mobile || '',
      email: email || '',
      registrationStatus: registrationStatus
        ? find(
          defaultRegistrationStatus,
          (reg) => reg.value === registrationStatus,
        )?.label
        : '',
      payFrequency: payFrequency
        ? find(defaultPaymentFrequency, (reg) => reg.value === payFrequency)
          ?.label
        : '',
      payMethod: payMethod
        ? find(defaultPaymentMethod, (p) => p.value === payMethod)?.label
        : '',
      jobDescription: jobDescription || '',
      rateHour: rateHour || '',
      hours: hours || '',
      workingTimeRegulation: workingTimeRegulation
        ? find(
          regulationOptions,
          (item) => item.value === workingTimeRegulation,
        )?.label
        : '',
      agencyConductRegulation: agencyConductRegulation
        ? find(
          regulationOptions,
          (item) => item.value === agencyConductRegulation,
        )?.label
        : '',
      dateOfJoining: dateOfJoining
        ? moment(dateOfJoining).format(DATE_FORMAT)
        : '',
      allowPortalLogin: getBooleanValue(allowPortalLogin),
      loginId: loginId || '',
      enableBenefits: isNil(setting?.enableBenefits)
        ? ''
        : getBooleanValue(setting?.enableBenefits),
      rightToWorkStatus: rightToWorkStatus
        ? find(defaultRightToWorkStatus, (r) => r.value === rightToWorkStatus)
          ?.label
        : '',
      documentType: documentType
        ? find(defaultDocumentTypeOptions, (d) => d.value === documentType)
          ?.label
        : '',
      documentExpiryDate: documentExpiryDate
        ? moment(documentExpiryDate).format(DATE_FORMAT)
        : '',
      bankName: bankRef?.bankName || '',
      accountName: bankRef?.accountName || '',
      accountNumber: bankRef?.accountNumber || '',
      sortCode: bankRef?.sortCode || '',
      buildingSocietyNumber: bankRef?.buildingSocietyNumber || '',
      SWIFTNo: bankRef?.SWIFTNo || '',
      IBANReference: bankRef?.IBANReference || '',
      paymentTerm: setting?.paymentTerm
        ? find(defaultPaymentTerm, (pt) => pt.value === setting?.paymentTerm)
          ?.label
        : '',
      paymentModel: setting?.paymentModel
        ? find(defaultPaymentModel, (pt) => pt.value === setting?.paymentModel)
          ?.label
        : '',
      businessType: setting?.businessType
        ? find(
          defaultsBusinessTypeOptions,
          (bs) => bs.value === setting?.businessType,
        )?.label
        : '',
      cisStatus: setting?.cisStatus
        ? find(defaultCisStatus, (cs) => cs.value === setting?.cisStatus)?.label
        : '',
      PVNo: setting?.personalVerificationNumber || '',
      VATRegNo: setting?.VATRegNo || '',
      verificationDate: setting?.verificationDate
        ? moment(setting?.verificationDate).format(DATE_FORMAT)
        : '',
      matchDate: setting?.matchDate
        ? moment(setting?.matchDate).format(DATE_FORMAT)
        : '',
      companyName: setting?.companyName || '',
      registrationNumber: setting?.registrationNumber || '',
      UTRNo: setting?.UTRNo || '',
      firmName: setting?.firmName || '',
      partnersName: setting?.partnersName || '',
      partnersCategory: setting?.partnersCategory
        ? find(
          defaultPartnerCategoryOptions,
          (item) => item.value === setting?.partnersCategory,
        )?.label
        : '',
      taxCode: setting?.taxCode || '',
      taxBasis: !!setting?.taxBasis
        ? setting?.taxBasis === 'W1M1'
          ? 'W1M1'
          : 'Cumulative'
        : '',
      niCategory: setting?.niCategory
        ? find(NICategoryOptions, (item) => item.value === setting?.niCategory)
          ?.label
        : '',
      workplacePostcode: setting?.workplacePostcode || '',
      starterType: setting?.starterType
        ? find(starterTypeOptions, (st) => st.value === setting?.starterType)
          ?.label
        : '',
      // payrollIdUnknown: getBooleanValue(!setting?.payrollIdKnown),
      previousPayrollNo: setting?.previousPayrollNo || '',
      starterDeclaration: setting?.starterDeclaration
        ? find(
          starterDeclarationOptions,
          (st) => st.value === setting?.starterDeclaration,
        )?.label
        : '',
      p45GrossToDate: setting?.p45GrossToDate || '',
      p45TaxToDate: setting?.p45TaxToDate || '',
      isApprentice: getBooleanValue(setting?.isApprentice),
      eeNI: p11?.eeNI,
      erNI: typeof p11?.erNI === 'number' ? p11?.erNI : '',
      eePension: typeof p11?.eePension === 'number' ? p11?.eePension : '',
      erPension: typeof p11?.erPension === 'number' ? p11?.erPension : '',
      stLoan: typeof p11?.stLoan === 'number' ? p11?.stLoan : '',
      pgLoan: typeof p11?.pgLoan === 'number' ? p11?.pgLoan : '',
      smp: typeof p11?.smp === 'number' ? p11?.smp : '',
      // sncp: typeof p11?.sncp === 'number' ? p11?.sncp : '',
      ssp: typeof p11?.ssp === 'number' ? p11?.ssp : '',
      sap: typeof p11?.sap === 'number' ? p11?.sap : '',
      // shpp: typeof p11?.shpp === 'number' ? p11?.shpp : '',
      spp: typeof p11?.spp === 'number' ? p11?.spp : '',
      grossToLel: typeof p11?.grossToLel === 'number' ? p11?.grossToLel : '',
      grossLelToPt:
        typeof p11?.grossLelToPt === 'number' ? p11?.grossLelToPt : '',
      grossPtToUEL:
        typeof p11?.grossPtToUEL === 'number' ? p11?.grossPtToUEL : '',
      //
      gross: typeof p11?.gross === 'number' ? p11?.gross : '',
      grossForTax: isNumber(p11?.grossForTax) ? p11?.grossForTax : '',
      grossForNI: isNumber(p11?.grossForNI) ? p11?.grossForNI : '',
      tax: isNumber(p11?.tax) ? p11?.tax : '',
      companyPayment: companyPayment?.id || '',
      marginMethod: setting?.marginMethod
        ? find(
          defaultMarginMethod,
          (reg) => reg.value === setting?.marginMethod,
        )?.label
        : '',
      margin: setting?.margin || '',
      minMargin: setting?.minMargin || '',
      marginCap: setting?.marginCapped || '',
      assignmentRequired: getBooleanValue(setting?.assignmentRequired),
      timesheetApprovalNeeded: getTimeSheetApprovalNeededFormat(
        setting?.timesheetApprovalNeeded,
      ),
      timesheetApprover: timesheetApprover?.length
        ? timesheetApprover.map((ag: any) => ag.name).join(',')
        : null,
      holidayPayMethod: setting?.holidayPayMethod
        ? find(
          holidayPaymentMethodOptions,
          (hp) => hp.value === setting?.holidayPayMethod,
        )?.label
        : '',
      includeInTimesheetRate: getBooleanValue(setting?.includeInTimesheetRate),
      holidayPayRate: setting?.holidayPayRate || '',
      reclaimAppLevy: getBooleanValue(setting?.reclaimAppLevy),
      holidayPayOpeningBalance:
        typeof setting?.holidayPayOpeningBalance === 'number'
          ? setting?.holidayPayOpeningBalance
          : '',
      retainedHolidayPayAppLevy:
        typeof setting?.retainedHolidayPayAppLevy === 'number'
          ? setting?.retainedHolidayPayAppLevy
          : '',
      retainedHolidayPayERNI:
        typeof setting?.retainedHolidayPayERNI === 'number'
          ? setting?.retainedHolidayPayERNI
          : '',
      retainedHolidayPayErPen:
        typeof setting?.retainedHolidayPayErPen === 'number'
          ? setting?.retainedHolidayPayErPen
          : '',
      expenseClaimRule: setting?.expenseClaimRule
        ? find(
          expenseClaimRuleOptions,
          (item) => item.value === setting?.expenseClaimRule,
        )?.label
        : '',
      isRequiredAttachmentExpenses: getBooleanValue(
        setting?.isRequiredAttachmentExpenses,
      ),
      expenseOpeningBalance: '',
      // pension setting
      outsideUKWorker: getBooleanValue(pensionSetting?.outsideUKWorker),
      erPensionDeductionMethod: erPensionDeductionMethod || '',
      erPensionPercentageOrAmount: erPensionPercentageOrAmount ?? '',
      eePensionDeductionMethod: eePensionDeductionMethod || '',
      eePensionPercentageOrAmount: eePensionPercentageOrAmount ?? '',
      pensionMethodText: pensionMethodText || '',
      nestGroupText: nestGroupText || '',
      dateOnClassifiedOn: pensionSetting?.dateOnClassifiedOn
        ? moment(pensionSetting?.dateOnClassifiedOn).format(DATE_FORMAT)
        : '',
      lastAssessmentOn: pensionSetting?.lastAssessmentOn
        ? moment(pensionSetting?.lastAssessmentOn).format(DATE_FORMAT)
        : '',
      postponementEndDate: pensionSetting?.postponementEndDate
        ? moment(pensionSetting?.postponementEndDate).format(DATE_FORMAT)
        : '',
      memberId: pensionSetting?.memberId || '',
      membershipStartDate: pensionSetting?.membershipStartDate
        ? moment(pensionSetting?.membershipStartDate).format(DATE_FORMAT)
        : '',
      otpState: pensionSetting?.otpState
        ? find(
          optStatePensionOptions,
          (item) => item.value === pensionSetting?.otpState,
        )?.label
        : '',
      otpInDate: pensionSetting?.otpInDate
        ? moment(pensionSetting?.otpInDate).format(DATE_FORMAT)
        : '',
      otpOutDate: pensionSetting?.otpOutDate
        ? moment(pensionSetting?.otpOutDate).format(DATE_FORMAT)
        : '',
      category: pensionSetting?.category || '',
      customerSuccessManager: '',
      agencyConsultant: '',
      salesConsultant: '',
      other: '',
      emergencyName: emergencyName || '',
      emergencyAddress1: emergencyAddress ? emergencyAddress[0] : '',
      emergencyAddress2: emergencyAddress ? emergencyAddress[1] : '',
      emergencyAddress3: emergencyAddress ? emergencyAddress[2] : '',
      emergencyMobile: emergencyMobile || '',
      emergencyEmail: emergencyEmail || '',
      emergencyCity: emergencyCity || '',
      emergencyCounty: emergencyCounty || '',
      emergencyPostCode: emergencyPostCode || '',
      emergencyCountry: emergencyCountry
        ? find(countryOptions, (item) => item?.value === emergencyCountry)
          ?.label
        : '',
      shareCodeVerified: shareCodeVerified || '',
      offPayrollWorker: isNil(setting?.offPayrollWorker)
        ? ''
        : getBooleanValue(setting?.offPayrollWorker),
      irregularPaidWorker: isNil(setting?.irregularPaidWorker)
        ? ''
        : getBooleanValue(setting?.irregularPaidWorker),
      // restrictedHours: getBooleanValue(isRestrictedHours),
      noOfWeeksWorked: setting?.noOfWeeksWorked || '',
    };
    return exportData;
  });
};
